import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';

function bytes(n) {
  if (n == null) return '-';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0) + ' ' + u[i];
}

function fmtDate(s) {
  if (!s) return '-';
  return new Date(s.replace(' ', 'T') + 'Z').toLocaleString('ko-KR');
}

function DiskMeter({ disk }) {
  if (!disk) return null;
  const pct = disk.used_percent_of_usable;
  const isHigh = pct >= 80;
  const isCrit = pct >= disk.auto_delete_threshold_percent;
  const color = isCrit ? 'bg-red-500' : isHigh ? 'bg-amber-500' : 'bg-medical-500';
  return (
    <div className="bg-white border border-medical-100 rounded-lg p-3 shadow-card">
      <div className="flex items-center justify-between text-xs text-medical-600">
        <span>가용 디스크 사용량 (예약공간 {bytes(disk.reserved_bytes)} 제외)</span>
        <span className="font-semibold text-medical-800">{bytes(disk.used_bytes)} / {bytes(disk.usable_total_bytes)} ({pct.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-medical-50 rounded mt-2 overflow-hidden">
        <div className={'h-full ' + color} style={{ width: Math.min(100, pct) + '%' }} />
      </div>
      <div className="mt-2 text-xs text-medical-500">
        ⚠ 디스크 사용률이 <b>{disk.auto_delete_threshold_percent}%</b> 를 초과하면
        가장 오래된 파일 버전부터 자동 삭제됩니다. 중요 파일은 미리 백업해 주세요.
      </div>
    </div>
  );
}

export default function FilesPage() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [disk, setDisk] = useState(null);
  const [selected, setSelected] = useState(null);     // folder id (null = all)
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [expanded, setExpanded] = useState({});       // file id -> versions
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragOverFolder, setDragOverFolder] = useState(null);

  const refresh = useCallback(async () => {
    const [fs, fls, dk] = await Promise.all([
      api.listFolders(),
      api.listFiles({ folder_id: selected || '', q: search, tag: tagFilter }),
      api.disk(),
    ]);
    setFolders(fs);
    setFiles(fls);
    setDisk(dk);
  }, [selected, search, tagFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Drop into the file area => upload to current folder
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const fd = new FormData();
    for (const f of acceptedFiles) fd.append('files', f);
    if (selected) fd.append('folder_id', selected);
    if (tagInput.trim()) fd.append('tags', JSON.stringify(tagInput.split(',').map(s => s.trim()).filter(Boolean)));
    try {
      await api.uploadFiles(fd);
      setTagInput('');
      await refresh();
    } catch (e) { alert(e.message); }
  }, [selected, tagInput, refresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: true });

  const newFolder = async () => {
    const name = window.prompt('새 폴더 이름');
    if (!name) return;
    try { await api.createFolder({ name }); refresh(); } catch (e) { alert(e.message); }
  };

  const beginRename = (f) => { setRenamingId(f.id); setRenameValue(f.name); };
  const commitRename = async () => {
    try { await api.renameFolder(renamingId, renameValue); setRenamingId(null); refresh(); }
    catch (e) { alert(e.message); }
  };
  const removeFolder = async (f) => {
    if (!window.confirm(`'${f.name}' 폴더를 삭제하시겠습니까?`)) return;
    try { await api.deleteFolder(f.id); if (selected === f.id) setSelected(null); refresh(); }
    catch (e) { alert(e.message); }
  };

  const toggleVersions = async (file) => {
    if (expanded[file.id]) {
      const next = { ...expanded }; delete next[file.id]; setExpanded(next);
    } else {
      const versions = await api.fileVersions(file.id);
      setExpanded({ ...expanded, [file.id]: versions });
    }
  };

  const deleteVersion = async (file, v) => {
    if (!window.confirm(`${file.original_name} v${v.version} 을(를) 삭제할까요?`)) return;
    try {
      await api.deleteVersion(file.id, v.version);
      const versions = await api.fileVersions(file.id);
      setExpanded({ ...expanded, [file.id]: versions });
      refresh();
    } catch (e) { alert(e.message); }
  };

  // Drag file row -> drop on folder => move file to folder
  const onFileDragStart = (e, fileId) => {
    e.dataTransfer.setData('text/file-id', String(fileId));
    e.dataTransfer.effectAllowed = 'move';
  };
  const onFolderDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    const fid = e.dataTransfer.getData('text/file-id');
    if (!fid) return;
    try { await api.patchFile(Number(fid), { folder_id: folderId }); refresh(); }
    catch (e) { alert(e.message); }
  };

  const totalFilter = useMemo(() => folders.find(f => f.id === selected), [folders, selected]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="grid grid-cols-12 gap-5">
        <aside className="col-span-12 md:col-span-3 bg-white rounded-xl shadow-card border border-medical-100 p-3 h-fit">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-medical-800">폴더</div>
            <button onClick={newFolder} className="text-xs px-2 py-1 rounded bg-medical-50 hover:bg-medical-100 text-medical-700">+ 새 폴더</button>
          </div>
          <button onClick={() => setSelected(null)}
            onDragOver={(e) => { e.preventDefault(); setDragOverFolder('all'); }}
            onDragLeave={() => setDragOverFolder(null)}
            onDrop={(e) => onFolderDrop(e, null)}
            className={'w-full text-left px-2 py-1.5 rounded text-sm ' +
              (selected === null ? 'bg-medical-100 text-medical-800 font-semibold' : 'hover:bg-medical-50 text-medical-700') +
              (dragOverFolder === 'all' ? ' ring-2 ring-medical-400' : '')}>
            📂 모두
          </button>
          <div className="mt-1 space-y-0.5">
            {folders.map(f => (
              <div key={f.id}
                onDragOver={(e) => { e.preventDefault(); setDragOverFolder(f.id); }}
                onDragLeave={() => setDragOverFolder(null)}
                onDrop={(e) => onFolderDrop(e, f.id)}
                className={'group flex items-center justify-between px-2 py-1.5 rounded text-sm ' +
                  (selected === f.id ? 'bg-medical-100 text-medical-800 font-semibold' : 'hover:bg-medical-50 text-medical-700') +
                  (dragOverFolder === f.id ? ' ring-2 ring-medical-400' : '')}>
                {renamingId === f.id ? (
                  <input autoFocus className="flex-1 px-1 py-0.5 border border-medical-300 rounded text-sm"
                    value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename} onKeyDown={(e) => e.key === 'Enter' && commitRename()} />
                ) : (
                  <button onClick={() => setSelected(f.id)} className="flex-1 text-left">📁 {f.name} <span className="text-xs text-medical-400">({f.file_count})</span></button>
                )}
                <span className="hidden group-hover:flex items-center gap-1">
                  <button title="이름변경" onClick={() => beginRename(f)} className="text-xs text-medical-500 hover:text-medical-700">✎</button>
                  <button title="삭제" onClick={() => removeFolder(f)} className="text-xs text-red-500 hover:text-red-700">🗑</button>
                </span>
              </div>
            ))}
          </div>
        </aside>

        <section className="col-span-12 md:col-span-9 space-y-4">
          <DiskMeter disk={disk} />

          <div {...getRootProps({
            className: 'bg-white rounded-xl border-2 border-dashed border-medical-200 p-6 text-center transition ' +
              (isDragActive ? 'dropzone-active' : ''),
          })}>
            <input {...getInputProps()} />
            <div className="text-medical-700 font-semibold">
              파일을 이 영역으로 드래그&드롭 하거나
              <label className="ml-2 text-medical-600 underline cursor-pointer">
                <input type="file" multiple className="hidden" onChange={(e) => onDrop(Array.from(e.target.files || []))} />
                선택
              </label> 하세요
            </div>
            <div className="text-xs text-medical-500 mt-1">
              {totalFilter ? <>대상 폴더: <b>{totalFilter.name}</b></> : <>대상 폴더: <b>지정 없음 (루트)</b></>}
              · 같은 이름의 파일은 자동으로 새 버전으로 저장됩니다.
            </div>
            <div className="mt-2 flex items-center justify-center gap-2 text-xs">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                placeholder="태그 (쉼표로 구분)"
                className="px-2 py-1 border border-medical-200 rounded w-72" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="파일명 검색"
              className="px-3 py-1.5 border border-medical-200 rounded text-sm w-60" />
            <input value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="태그 필터"
              className="px-3 py-1.5 border border-medical-200 rounded text-sm w-40" />
            <button onClick={refresh} className="text-sm text-medical-700 hover:underline">새로고침</button>
            <span className="ml-auto text-xs text-medical-500">{files.length}개 파일</span>
          </div>

          <div className="bg-white rounded-xl shadow-card border border-medical-100 overflow-x-auto">
            <table className="w-full text-sm table-fixed min-w-[860px]">
              <colgroup>
                <col className="w-8" />
                <col />
                <col className="w-24" />
                <col className="w-44" />
                <col className="w-36" />
                <col className="w-20" />
                <col className="w-24" />
              </colgroup>
              <thead className="bg-medical-50 text-medical-700">
                <tr>
                  <th className="text-left px-3 py-2"></th>
                  <th className="text-left px-3 py-2">파일명</th>
                  <th className="text-left px-3 py-2">최신버전</th>
                  <th className="text-left px-3 py-2">업로더</th>
                  <th className="text-left px-3 py-2">최근 업로드</th>
                  <th className="text-left px-3 py-2">크기</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {files.map(file => (
                  <React.Fragment key={file.id}>
                    <tr draggable onDragStart={(e) => onFileDragStart(e, file.id)} className="border-t border-medical-50 hover:bg-medical-50/40 align-top">
                      <td className="px-3 py-2">
                        <button onClick={() => toggleVersions(file)} className="text-medical-600">{expanded[file.id] ? '▼' : '▶'}</button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-medical-800 break-all">{file.original_name}</div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {file.tags?.map(t => (
                            <span key={t} className="text-[10px] bg-medical-50 text-medical-700 border border-medical-100 rounded-full px-2 py-0.5">#{t}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-medical-700">v{file.latest_version} <span className="text-xs text-medical-400">({file.version_count}개)</span></td>
                      <td className="px-3 py-2 break-keep">
                        <div className="text-medical-800">{file.latest_uploader_name}</div>
                        <div className="text-[11px] text-medical-500">{file.latest_uploader_institution}</div>
                      </td>
                      <td className="px-3 py-2 text-medical-700 text-xs leading-tight break-keep">{fmtDate(file.latest_uploaded_at)}</td>
                      <td className="px-3 py-2 text-medical-700 break-keep">{bytes(file.total_size)}</td>
                      <td className="px-3 py-2 text-right">
                        <a href={api.downloadVersionUrl(file.id, file.latest_version)}
                           className="inline-block text-xs leading-tight text-medical-700 hover:text-medical-800 hover:underline break-keep whitespace-normal text-right">
                          최신버전<br/>다운로드 ↓
                        </a>
                      </td>
                    </tr>
                    {expanded[file.id] && (
                      <tr><td colSpan={7} className="bg-medical-50/40 px-6 py-3">
                        <div className="text-xs text-medical-500 mb-1">버전 이력</div>
                        <table className="w-full text-xs table-fixed">
                          <colgroup>
                            <col className="w-14" />
                            <col />
                            <col className="w-36" />
                            <col className="w-20" />
                            <col className="w-24" />
                          </colgroup>
                          <thead className="text-medical-600">
                            <tr>
                              <th className="text-left py-1">버전</th>
                              <th className="text-left py-1">업로더</th>
                              <th className="text-left py-1">업로드 일시</th>
                              <th className="text-left py-1">크기</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {expanded[file.id].map(v => {
                              const mine = v.uploaded_by === user.id;
                              return (
                                <tr key={v.id} className="border-t border-medical-100 align-top">
                                  <td className="py-1.5">v{v.version}</td>
                                  <td className="py-1.5 break-keep">
                                    <span className="text-medical-800">{v.uploaded_by_name}</span>
                                    <span className="text-medical-400 ml-2">{v.uploaded_by_institution}</span>
                                  </td>
                                  <td className="py-1.5 leading-tight break-keep">{fmtDate(v.uploaded_at)}</td>
                                  <td className="py-1.5 break-keep">{bytes(v.size_bytes)}</td>
                                  <td className="py-1.5 text-right break-keep">
                                    <a className="text-medical-600 hover:underline mr-3"
                                       href={api.downloadVersionUrl(file.id, v.version)}>다운로드</a>
                                    {mine && (
                                      <button onClick={() => deleteVersion(file, v)} className="text-red-500 hover:underline">삭제</button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {files.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-medical-400">파일이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
